import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Camera, Loader2, Video } from "lucide-react";
import { toast } from "sonner";
import {
  PhotoSessionCameraView,
  type PhotoSessionCameraHandle,
} from "./PhotoSessionCameraView";
import {
  PhotoSessionThumbnailStrip,
  type StripDisplayItem,
} from "./PhotoSessionThumbnailStrip";
import { PhotoSessionRotateOverlay } from "./PhotoSessionRotateOverlay";
import { PhotoSessionReviewView } from "./PhotoSessionReviewView";
import { usePhotoSessionOrientation } from "./usePhotoSessionOrientation";
import { photoSessionRootClass, photoSessionTheme as theme } from "./photoSessionTheme";
import {
  getGuideForPhotoIndex,
  resolveLabelsForPhotoIndex,
} from "./photoSessionGuideContext";
import type { ListingPhotoQueueItem } from "@/hooks/useListingPhotoUploadQueue";
import {
  getSectionImageCount,
  getSectionMax,
  sectionRequiresLandscape,
  type PhotoSessionSection,
} from "./photoSessionUtils";
import { ENGINE_VIDEO_GUIDE } from "./exteriorCarGuides";
import type { VehicleTestImages } from "@/types/vehicleTest";
import { isVideoMedia } from "@/lib/isVideoMedia";
import { useIntercomLauncherVisibility } from "@/context/IntercomLauncherVisibilityContext";
import "./photoSession.css";

const PHOTO_SESSION_Z = 10050;

const safeTop = "max(0.75rem, env(safe-area-inset-top, 0px))";
const safeBottom = "max(0.75rem, env(safe-area-inset-bottom, 0px))";
const safeLeft = "max(0.75rem, env(safe-area-inset-left, 0px))";
const safeRight = "max(0.75rem, env(safe-area-inset-right, 0px))";

export interface PhotoSessionLabels {
  takePhoto: string;
  done: string;
  countOfMax: string;
  maxReached: string;
  startCamera: string;
  cameraStarting: string;
  cameraError: string;
  fallbackLabel: string;
  captureHint: string;
  statusPending: string;
  statusUploading: string;
  statusDone: string;
  statusFailed: string;
  removePhoto: string;
  retry: string;
  stripPlaceholder: string;
  landscapeGuide: string;
  landscapeFormatHint: string;
  processingPhoto: string;
  rotatePhoneHint?: string;
  rotateRequiredTitle?: string;
  hideGuide?: string;
  showGuide?: string;
  cameraNotReady?: string;
  sectionShotTitle?: string;
  exteriorGuideStep?: string;
  interiorGuideStep?: string;
  guidedOptionalHint?: string;
  guidedOptionalSub?: string;
  exteriorOptionalHint?: string;
  exteriorOptionalSub?: string;
  exteriorGuides?: Record<string, string>;
  interiorGuides?: Record<string, string>;
  retake?: string;
  nextPhoto?: string;
}

interface PhotoSessionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sectionTitle: string;
  section: PhotoSessionSection;
  tireType?: "primary" | "secondary";
  /** When true, only the engine-video step is shown (opened from Motorvideo). */
  engineOnly?: boolean;
  currentImages: VehicleTestImages;
  sessionId: string | null;
  sessionItems: ListingPhotoQueueItem[];
  onCapture: (blob: Blob) => boolean;
  onReplace: (id: string, blob: Blob) => boolean;
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
  countInFlightForSection: number;
  labels: PhotoSessionLabels;
  maxReachedToast: string;
}

export function PhotoSessionModal({
  open,
  onOpenChange,
  sectionTitle,
  section,
  tireType,
  engineOnly = false,
  currentImages,
  sessionId,
  sessionItems,
  onCapture,
  onReplace,
  onRemove,
  onRetry,
  countInFlightForSection,
  labels,
  maxReachedToast,
}: PhotoSessionModalProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [retakeItemId, setRetakeItemId] = useState<string | null>(null);
  const [retakePhotoIndex, setRetakePhotoIndex] = useState<number | null>(null);
  const [captureLocked, setCaptureLocked] = useState(false);
  const [isCameraRecording, setIsCameraRecording] = useState(false);
  const [optimisticItem, setOptimisticItem] = useState<StripDisplayItem | null>(
    null,
  );
  const optimisticUrlRef = useRef<string | null>(null);
  const pendingCaptureIdRef = useRef<string | null>(null);
  const closingRef = useRef(false);
  const sessionCountAtCaptureRef = useRef(0);
  const cameraRef = useRef<PhotoSessionCameraHandle>(null);
  const canCaptureMoreRef = useRef(true);
  const { isLandscape } = usePhotoSessionOrientation();
  const { setSuppressIntercomLauncher } = useIntercomLauncherVisibility();

  const requiresLandscape = sectionRequiresLandscape(section);
  const stripOnBottom = !requiresLandscape;
  const showRotateOverlay = requiresLandscape && !isLandscape;

  const hasEngineVideo = (currentImages.exterior || []).some((img) =>
    isVideoMedia(img.type, img.url),
  );
  const maxForSection = engineOnly ? 1 : getSectionMax(section);
  const uploadedCount = engineOnly
    ? hasEngineVideo
      ? 1
      : 0
    : getSectionImageCount(currentImages, section, tireType);

  // Count pending/uploading/done so a fast upload cannot free a slot before
  // currentImages reflects the new picture (that race caused >max uploads).
  const sessionOccupied = sessionItems.filter(
    (i) =>
      i.status === "pending" ||
      i.status === "uploading" ||
      i.status === "done",
  ).length;
  const remaining = Math.max(
    0,
    maxForSection - Math.max(uploadedCount, sessionOccupied),
  );
  const canCaptureMore = remaining > 0;
  canCaptureMoreRef.current = canCaptureMore;

  const photosCommitted = Math.max(uploadedCount, sessionOccupied);
  const displayCount = canCaptureMore
    ? Math.min(photosCommitted + 1, maxForSection)
    : Math.min(photosCommitted, maxForSection);

  const countLabel = labels.countOfMax
    .replace("{current}", String(displayCount))
    .replace("{max}", String(maxForSection));

  const maxReachedLabel = labels.maxReached.replace(
    "{max}",
    String(maxForSection),
  );

  const nextPhotoIndex = Math.min(
    Math.max(uploadedCount, sessionOccupied) + 1,
    maxForSection,
  );

  const displayStripItems = useMemo((): StripDisplayItem[] => {
    const base = sessionItems as StripDisplayItem[];
    if (!optimisticItem) return base;
    if (sessionItems.length > sessionCountAtCaptureRef.current) {
      return base;
    }
    const withoutDup = base.filter((i) => i.id !== optimisticItem.id);
    return [...withoutDup, optimisticItem];
  }, [sessionItems, optimisticItem]);

  const selectedItemIndex = useMemo(() => {
    if (!selectedId) return -1;
    return displayStripItems.findIndex((i) => i.id === selectedId);
  }, [displayStripItems, selectedId]);

  const selectedItem =
    selectedItemIndex >= 0 ? displayStripItems[selectedItemIndex] : null;

  const reviewPhotoIndex =
    selectedItem?.photoIndex ??
    (selectedItemIndex >= 0 ? uploadedCount + selectedItemIndex + 1 : null);

  const isRetakeMode = Boolean(retakeItemId);
  const isReviewMode = Boolean(
    selectedItem?.previewUrl && !isRetakeMode && !captureLocked,
  );

  const cameraPhotoIndex = isRetakeMode
    ? (retakePhotoIndex ?? nextPhotoIndex)
    : nextPhotoIndex;

  const { activeGuide, guideFrameMode } = engineOnly
    ? { activeGuide: ENGINE_VIDEO_GUIDE, guideFrameMode: "guided" as const }
    : getGuideForPhotoIndex(section, cameraPhotoIndex);

  const labelContext = useMemo(
    () => ({
      exteriorGuides: labels.exteriorGuides,
      interiorGuides: labels.interiorGuides,
      exteriorGuideStep: labels.exteriorGuideStep,
      interiorGuideStep: labels.interiorGuideStep,
      guidedOptionalHint: labels.guidedOptionalHint,
      exteriorOptionalHint: labels.exteriorOptionalHint,
      guidedOptionalSub: labels.guidedOptionalSub,
      exteriorOptionalSub: labels.exteriorOptionalSub,
      sectionShotTitle: labels.sectionShotTitle,
      landscapeGuide: labels.landscapeGuide,
      landscapeFormatHint: labels.landscapeFormatHint,
    }),
    [labels],
  );

  const guideLabels = useMemo(() => {
    if (engineOnly) {
      const shotName =
        labels.exteriorGuides?.engine ??
        labels.exteriorGuides?.engineChecklistTitle ??
        sectionTitle;
      return {
        floatingTitle: shotName,
        landscapeGuide: shotName,
        landscapeFormatHint:
          labels.exteriorGuides?.engineChecklistTitle ?? shotName,
      };
    }
    const indexForTitle = isReviewMode
      ? (reviewPhotoIndex ?? nextPhotoIndex)
      : cameraPhotoIndex;
    return resolveLabelsForPhotoIndex(
      section,
      sectionTitle,
      indexForTitle,
      maxForSection,
      labelContext,
    );
  }, [
    engineOnly,
    isReviewMode,
    reviewPhotoIndex,
    cameraPhotoIndex,
    nextPhotoIndex,
    section,
    sectionTitle,
    maxForSection,
    labelContext,
    labels.exteriorGuides,
  ]);

  const cameraActive = open && !showRotateOverlay && !isReviewMode;
  const showShutter = cameraActive;

  const clearOptimistic = useCallback(() => {
    if (optimisticUrlRef.current) {
      URL.revokeObjectURL(optimisticUrlRef.current);
      optimisticUrlRef.current = null;
    }
    setOptimisticItem(null);
  }, []);

  /** True when the user tapped Klar while a clip was still recording. */
  const closeAfterCaptureRef = useRef(false);

  const doClose = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;
    setSelectedId(null);
    setRetakeItemId(null);
    setRetakePhotoIndex(null);
    // Keep the overlay mounted briefly so the synthetic click after a touch
    // (Klar) cannot fall through to CreateTestDialog close / native Home.
    const shield = document.createElement("div");
    shield.setAttribute("data-photo-session-click-shield", "1");
    Object.assign(shield.style, {
      position: "fixed",
      inset: "0",
      zIndex: String(PHOTO_SESSION_Z + 1),
      pointerEvents: "auto",
      background: "transparent",
    });
    const swallow = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };
    shield.addEventListener("click", swallow, true);
    shield.addEventListener("pointerup", swallow, true);
    shield.addEventListener("touchend", swallow, true);
    document.body.appendChild(shield);
    window.setTimeout(() => {
      shield.remove();
    }, 450);
    onOpenChange(false);
  }, [onOpenChange]);

  const finishPendingClose = useCallback(() => {
    if (!closeAfterCaptureRef.current) return;
    closeAfterCaptureRef.current = false;
    doClose();
  }, [doClose]);

  const handleCapture = useCallback(
    (blob: Blob) => {
      if (!sessionId) {
        pendingCaptureIdRef.current = null;
        setCaptureLocked(false);
        finishPendingClose();
        return;
      }

      if (retakeItemId) {
        const replaceId = retakeItemId;
        clearOptimistic();
        pendingCaptureIdRef.current = null;
        const ok = onReplace(replaceId, blob);
        setCaptureLocked(false);
        setRetakeItemId(null);
        setRetakePhotoIndex(null);
        if (!ok) {
          toast.error(maxReachedToast.replace("{max}", String(maxForSection)));
        }
        setSelectedId(null);
        finishPendingClose();
        return;
      }

      if (!canCaptureMoreRef.current) {
        pendingCaptureIdRef.current = null;
        clearOptimistic();
        setCaptureLocked(false);
        toast.error(maxReachedToast.replace("{max}", String(maxForSection)));
        finishPendingClose();
        return;
      }

      const previewUrl = URL.createObjectURL(blob);
      optimisticUrlRef.current = previewUrl;
      const optId = pendingCaptureIdRef.current ?? `opt-${Date.now()}`;
      pendingCaptureIdRef.current = null;
      setOptimisticItem({
        id: optId,
        sessionId,
        blob,
        previewUrl,
        status: "pending",
        section,
        tireType,
        isOptimistic: true,
        photoIndex: nextPhotoIndex,
      });

      const ok = onCapture(blob);
      setCaptureLocked(false);

      if (!ok) {
        clearOptimistic();
        toast.error(maxReachedToast.replace("{max}", String(maxForSection)));
      }
      finishPendingClose();
    },
    [
      sessionId,
      section,
      tireType,
      maxForSection,
      maxReachedToast,
      onCapture,
      onReplace,
      retakeItemId,
      clearOptimistic,
      finishPendingClose,
      nextPhotoIndex,
    ],
  );

  const handleCaptureFailed = useCallback(
    (reason: string) => {
      pendingCaptureIdRef.current = null;
      clearOptimistic();
      setCaptureLocked(false);
      finishPendingClose();
      if (reason === "max_reached") {
        toast.error(maxReachedToast.replace("{max}", String(maxForSection)));
        return;
      }
      if (reason === "video_not_ready") {
        toast.error(
          labels.cameraNotReady ??
            labels.cameraStarting ??
            "Kameran är inte redo – försök igen om ett ögonblick",
        );
      }
    },
    [
      clearOptimistic,
      finishPendingClose,
      labels.cameraNotReady,
      labels.cameraStarting,
      maxForSection,
      maxReachedToast,
    ],
  );

  const handleClose = useCallback(() => {
    // Klar while an engine clip is still recording: stop the recorder and close
    // only after the clip has been handed to the upload queue (otherwise it is lost).
    if (isCameraRecording && cameraRef.current) {
      closeAfterCaptureRef.current = true;
      cameraRef.current.takePhoto();
      return;
    }
    doClose();
  }, [isCameraRecording, doClose]);

  const handleSelectStripItem = useCallback((id: string) => {
    setRetakeItemId(null);
    setRetakePhotoIndex(null);
    setSelectedId(id);
  }, []);

  const handleContinueCapture = useCallback(() => {
    setSelectedId(null);
    setRetakeItemId(null);
    setRetakePhotoIndex(null);
  }, []);

  const handleDeleteSelected = useCallback(() => {
    if (!selectedItem) return;
    onRemove(selectedItem.id);
    handleContinueCapture();
  }, [selectedItem, onRemove, handleContinueCapture]);

  const handleRetakeSelected = useCallback(() => {
    if (!selectedItem || selectedItem.status === "uploading") return;
    const index = selectedItem.photoIndex ?? reviewPhotoIndex;
    if (index == null) return;
    setRetakePhotoIndex(index);
    setRetakeItemId(selectedItem.id);
  }, [selectedItem, reviewPhotoIndex]);

  const canEditSelected =
    selectedItem?.status === "pending" ||
    selectedItem?.status === "uploading" ||
    selectedItem?.status === "failed" ||
    selectedItem?.status === "done";

  const handleShutterClick = useCallback(() => {
    if (!sessionId) return;
    // Second tap while recording stops the clip (do not re-lock / re-optimistic).
    if (isCameraRecording) {
      cameraRef.current?.takePhoto();
      return;
    }
    if (captureLocked) return;
    if (!retakeItemId && !canCaptureMoreRef.current) return;
    sessionCountAtCaptureRef.current = sessionItems.length;
    const optId = retakeItemId ?? `opt-${Date.now()}`;
    pendingCaptureIdRef.current = retakeItemId ? null : optId;
    setCaptureLocked(true);
    if (!retakeItemId) {
      setOptimisticItem({
        id: optId,
        sessionId,
        blob: new Blob(),
        status: "pending",
        section,
        tireType,
        isOptimistic: true,
        photoIndex: nextPhotoIndex,
      });
    }
    cameraRef.current?.takePhoto();
  }, [
    captureLocked,
    isCameraRecording,
    sessionId,
    section,
    tireType,
    sessionItems.length,
    retakeItemId,
    nextPhotoIndex,
  ]);

  useEffect(() => {
    if (!optimisticItem) return;
    if (sessionItems.length > sessionCountAtCaptureRef.current) {
      clearOptimistic();
    }
  }, [sessionItems, optimisticItem, clearOptimistic]);

  useEffect(() => {
    if (!open) return;
    setSuppressIntercomLauncher(true);
    return () => setSuppressIntercomLauncher(false);
  }, [open, setSuppressIntercomLauncher]);

  useEffect(() => {
    if (!open) {
      closingRef.current = false;
      closeAfterCaptureRef.current = false;
      pendingCaptureIdRef.current = null;
      setCaptureLocked(false);
      setRetakeItemId(null);
      setRetakePhotoIndex(null);
      setSelectedId(null);
      clearOptimistic();
      return;
    }

    document.body.classList.add("photo-session-active");
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const blockTouchOnDocument = (e: TouchEvent) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (t.closest(".photo-session-overlay")) return;
      e.preventDefault();
    };

    document.addEventListener("touchmove", blockTouchOnDocument, {
      passive: false,
    });

    return () => {
      document.body.classList.remove("photo-session-active");
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("touchmove", blockTouchOnDocument);
    };
  }, [open, clearOptimistic]);

  if (!open || !sessionId) return null;

  const stripLabels = {
    statusPending: labels.statusPending,
    statusUploading: labels.statusUploading,
    statusDone: labels.statusDone,
    statusFailed: labels.statusFailed,
    removePhoto: labels.removePhoto,
    retry: labels.retry,
    emptyPlaceholder: labels.stripPlaceholder,
  };

  const cameraLabels = {
    takePhoto: labels.takePhoto,
    startCamera: labels.startCamera,
    cameraStarting: labels.cameraStarting,
    cameraError: labels.cameraError,
    fallbackLabel: labels.fallbackLabel,
    landscapeGuide: guideLabels.landscapeGuide,
    landscapeFormatHint: guideLabels.landscapeFormatHint,
    processingPhoto: labels.processingPhoto,
    hideGuide: labels.hideGuide,
    showGuide: labels.showGuide,
    cameraNotReady: labels.cameraNotReady,
  };

  return createPortal(
    <div
      className={`photo-session-overlay ${photoSessionRootClass} fixed inset-0 flex flex-col`}
      style={{
        zIndex: PHOTO_SESSION_Z,
        height: "100dvh",
        maxHeight: "100dvh",
        width: "100vw",
        maxWidth: "100vw",
        paddingTop: safeTop,
        paddingBottom: safeBottom,
        paddingLeft: safeLeft,
        paddingRight: safeRight,
        backgroundColor: theme.bg,
        isolation: "isolate",
      }}
      role="dialog"
      aria-modal="true"
      aria-label={guideLabels.floatingTitle}
    >
      <div
        className={
          stripOnBottom
            ? "flex-1 flex flex-col min-h-0 overflow-hidden"
            : "flex-1 flex flex-row min-h-0 overflow-hidden"
        }
      >
        {!stripOnBottom && (
          <PhotoSessionThumbnailStrip
            layout="vertical"
            items={displayStripItems}
            selectedId={selectedId}
            onSelect={handleSelectStripItem}
            onRemove={onRemove}
            onRetry={onRetry}
            labels={stripLabels}
          />
        )}

        <div className="flex-1 relative min-w-0 min-h-0 flex flex-col">
          <div className="flex-1 relative min-h-0 min-w-0">
            <PhotoSessionCameraView
              ref={cameraRef}
              active={cameraActive}
              canCaptureMore={canCaptureMore || isRetakeMode}
              showLandscapeGuide={requiresLandscape && !isReviewMode}
              guideImageSrc={activeGuide?.imageSrc ?? null}
              guideScale={activeGuide?.scale ?? 1}
              guideWidthPct={activeGuide?.widthPct ?? 90}
              guideMaxHeightPct={activeGuide?.maxHeightPct ?? 76}
              guideFrameMode={guideFrameMode}
              showShutter={false}
              captureMode={activeGuide?.captureMode ?? "photo"}
              maxVideoSeconds={activeGuide?.maxVideoSeconds ?? 10}
              onRecordingChange={setIsCameraRecording}
              onCapture={handleCapture}
              onCaptureFailed={handleCaptureFailed}
              className="absolute inset-0"
              labels={{
                ...cameraLabels,
                startRecording:
                  labels.exteriorGuides?.startRecording ?? "Spela in",
                stopRecording:
                  labels.exteriorGuides?.stopRecording ?? "Stoppa",
                recording: labels.exteriorGuides?.recording ?? "Spelar in",
                engineChecklistTitle:
                  labels.exteriorGuides?.engineChecklistTitle ??
                  "Filma motorn (~10 sek)",
                engineChecklistIdle:
                  labels.exteriorGuides?.engineChecklistIdle ??
                  "1. Sätt på motorn i tomgång",
                engineChecklistFilm:
                  labels.exteriorGuides?.engineChecklistFilm ??
                  "2. Filma motorrummet stadigt i cirka 10 sekunder",
              }}
            />

            {isReviewMode && selectedItem?.previewUrl && (
              <PhotoSessionReviewView
                previewUrl={selectedItem.previewUrl}
                status={selectedItem.status}
                canEdit={canEditSelected}
                onRemove={handleDeleteSelected}
                onRetake={handleRetakeSelected}
                onContinueCapture={handleContinueCapture}
                isVideo={Boolean(
                  selectedItem.blob?.type?.startsWith("video/") ||
                    selectedItem.previewUrl.includes(".webm") ||
                    selectedItem.previewUrl.includes(".mp4"),
                )}
                labels={{
                  removePhoto: labels.removePhoto,
                  retake: labels.retake,
                  nextPhoto: labels.nextPhoto,
                  uploading: labels.statusUploading,
                  uploaded: labels.statusDone,
                }}
              />
            )}

            {/* Floating chrome — above rotate overlay (z-80) */}
            <div
              className={`absolute top-2 left-2 right-2 flex items-start justify-between gap-2 pointer-events-none ${
                showRotateOverlay ? "z-[100]" : "z-[100]"
              }`}
            >
              <div
                className="pointer-events-auto rounded-lg backdrop-blur-md px-3 py-1.5 min-w-0 max-w-[min(72%,18rem)] shadow-lg"
                style={{ backgroundColor: "rgba(0,0,0,0.82)" }}
              >
                <p
                  className="text-sm font-bold leading-snug line-clamp-2"
                  style={{ color: theme.text }}
                >
                  {guideLabels.floatingTitle}
                </p>
                <p
                  className="text-[11px] mt-0.5 font-medium"
                  style={{
                    color: !canCaptureMore ? theme.accent : theme.textMuted,
                  }}
                >
                  {countLabel}
                </p>
                {!canCaptureMore && (
                  <p
                    className="text-[10px] mt-1 font-semibold leading-snug"
                    style={{ color: theme.accent }}
                  >
                    {maxReachedLabel}
                  </p>
                )}
              </div>
              <button
                type="button"
                className="pointer-events-auto shrink-0 rounded-lg px-4 py-2 text-sm font-semibold shadow-xl ring-2 ring-white/25 active:scale-95 transition-transform touch-manipulation"
                style={{ backgroundColor: theme.accent, color: theme.accentFg }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClose();
                }}
              >
                {labels.done}
              </button>
            </div>

            {showShutter && !canCaptureMore && !isRetakeMode && (
              <div
                className="absolute z-[195] left-3 right-[5.5rem] flex justify-center pointer-events-none top-1/2 -translate-y-[calc(50%+3rem)]"
              >
                <div
                  className="rounded-xl px-4 py-2.5 text-center shadow-lg border max-w-md"
                  style={{
                    backgroundColor: "rgba(0, 0, 0, 0.72)",
                    borderColor: `${theme.accent}66`,
                  }}
                >
                  <p
                    className="text-sm font-semibold leading-snug"
                    style={{ color: theme.accent }}
                  >
                    {maxReachedLabel}
                  </p>
                </div>
              </div>
            )}

            {showShutter && (
              <button
                type="button"
                onClick={handleShutterClick}
                disabled={
                  (!isCameraRecording && captureLocked) ||
                  (!isRetakeMode && !canCaptureMore && !isCameraRecording)
                }
                className="absolute z-[200] top-1/2 -translate-y-1/2 h-[4.25rem] w-[4.25rem] rounded-full flex items-center justify-center shadow-2xl active:scale-95 transition-all border-4 touch-manipulation disabled:pointer-events-none"
                style={{
                  right: "max(0.75rem, env(safe-area-inset-right, 0px))",
                  backgroundColor: isCameraRecording ? "#dc2626" : theme.accent,
                  color: theme.accentFg,
                  borderColor: "rgba(255,255,255,0.35)",
                  opacity:
                    (!isCameraRecording && captureLocked) ||
                    (!isRetakeMode && !canCaptureMore && !isCameraRecording)
                      ? 0.4
                      : 1,
                }}
                aria-label={
                  isCameraRecording
                    ? (labels.exteriorGuides?.stopRecording ?? "Stoppa")
                    : isRetakeMode
                      ? (labels.retake ?? labels.takePhoto ?? "Retake")
                      : !canCaptureMore
                        ? maxReachedLabel
                        : captureLocked
                          ? (labels.processingPhoto ?? "Processing")
                          : activeGuide?.captureMode === "video"
                            ? (labels.exteriorGuides?.startRecording ??
                              "Spela in")
                            : (labels.takePhoto ?? "Take photo")
                }
                aria-disabled={
                  (!isCameraRecording && captureLocked) ||
                  (!isRetakeMode && !canCaptureMore && !isCameraRecording)
                }
              >
                {isCameraRecording ? (
                  <span className="h-7 w-7 rounded-sm bg-white pointer-events-none" />
                ) : captureLocked ? (
                  <Loader2 className="h-9 w-9 animate-spin pointer-events-none" />
                ) : activeGuide?.captureMode === "video" ? (
                  <Video className="h-9 w-9 pointer-events-none" />
                ) : (
                  <Camera className="h-9 w-9 pointer-events-none" />
                )}
              </button>
            )}

            {showRotateOverlay && (
              <PhotoSessionRotateOverlay
                title={
                  labels.rotateRequiredTitle ?? "Rotate your phone horizontally"
                }
                hint={
                  labels.rotatePhoneHint ??
                  "Landscape photos are required for exterior and interior only. Rotate your phone horizontally."
                }
              />
            )}
          </div>

          {stripOnBottom && (
            <PhotoSessionThumbnailStrip
              layout="horizontal"
              items={displayStripItems}
              selectedId={selectedId}
              onSelect={handleSelectStripItem}
              onRemove={onRemove}
              onRetry={onRetry}
              labels={stripLabels}
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
